import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, ShieldCheck, Paperclip, X } from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function inlineMd(s) {
  return String(s).split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g).map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if ((p.startsWith("*") && p.endsWith("*") && p.length > 2) || (p.startsWith("_") && p.endsWith("_") && p.length > 2))
      return <em key={i}>{p.slice(1, -1)}</em>;
    return <span key={i}>{p}</span>;
  });
}
function renderRich(text) {
  const lines = String(text).split("\n");
  const out = [];
  let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={"u" + out.length} className="my-1 list-disc space-y-0.5 pl-4">{list}</ul>); list = []; } };
  lines.forEach((raw, idx) => {
    const l = raw.trim();
    if (!l) { flush(); return; }
    if (/^(-{3,}|_{3,}|\*{3,})$/.test(l)) { flush(); return; } // horizontal rule -> skip
    if (/^\|?\s*[-:]+\s*(\|\s*[-:]+\s*)+\|?$/.test(l)) return; // table divider
    if (l.startsWith("|")) { const cells = l.split("|").map((c) => c.trim()).filter(Boolean); flush(); out.push(<p key={idx}>{inlineMd(cells.join(" · "))}</p>); return; }
    const h = l.match(/^#{1,6}\s+(.*)/);
    if (h) { flush(); out.push(<p key={idx} className="mt-1 font-semibold text-slate-800">{inlineMd(h[1])}</p>); return; }
    const b = l.match(/^[-*]\s+(.*)/);
    if (b) { list.push(<li key={idx}>{inlineMd(b[1])}</li>); return; }
    flush(); out.push(<p key={idx}>{inlineMd(l.replace(/^>\s?/, ""))}</p>);
  });
  flush();
  return out;
}

export default function AIChat({ surface = "general", suggestions = [], compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if ((!msg && !file) || loading) return;
    setInput("");
    if (file) {
      const f = file; setFile(null);
      setMessages((m) => [...m, { role: "user", content: `${msg || "Process this file"}  📎 ${f.name}` }]);
      setLoading(true);
      try {
        const fd = new FormData();
        fd.append("file", f); fd.append("instruction", msg); fd.append("surface", surface);
        const { data } = await api.post("/ai/ingest", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const list = (data.items || []).map((t) => `- ${t}`).join("\n");
        const reply = data.created
          ? `Done — I added ${data.created} ${data.type}${data.created === 1 ? "" : "s"} from ${f.name}${data.skipped ? ` (skipped ${data.skipped})` : ""}.\n${list}`
          : "I couldn't find records to add in that file.";
        setMessages((m) => [...m, { role: "assistant", content: reply }]);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't process that file.");
        setMessages((m) => [...m, { role: "assistant", content: formatApiError(e.response?.data?.detail) || "I couldn't process that file. Please try a CSV, Excel, PDF or text file." }]);
      } finally { setLoading(false); }
      return;
    }
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId, surface });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI is unavailable right now.");
      setMessages((m) => [...m, { role: "assistant", content: "I'm unable to respond right now. Please try again shortly." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="ai-chat">
      <div ref={scrollRef} className="custom-scroll flex-1 space-y-3 overflow-y-auto pr-1">
        {messages.length === 0 && (
          <div className="rounded-xl bg-white/70 p-3 text-sm text-slate-600">
            <div className="mb-1 flex items-center gap-2 font-semibold text-slate-800">
              <Sparkles className="h-4 w-4 text-blue-500" /> Hi, I'm your AI Secretary
            </div>
            I can help you search, organize and track using your authorized data. I never make up prices, availability, or bookings.
          </div>
        )}
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "brand-gradient-bg whitespace-pre-wrap text-white" : "space-y-1 border border-slate-200 bg-white text-slate-700"}`}>
              {m.role === "user" ? m.content : renderRich(m.content)}
            </div>
          </motion.div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-400">
              <span className="inline-flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-blue-400" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-purple-400" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-pink-400" style={{ animationDelay: "300ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {messages.length === 0 && suggestions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => send(s)} data-testid={`ai-suggestion-${i}`}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 transition hover:border-blue-300 hover:text-blue-600">
              {s}
            </button>
          ))}
        </div>
      )}

      {file && (
        <div className="mt-2 flex items-center gap-2 self-start rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs text-blue-600" data-testid="ai-file-chip">
          <Paperclip className="h-3 w-3" /> <span className="max-w-[220px] truncate">{file.name}</span>
          <button onClick={() => setFile(null)} type="button" aria-label="Remove file"><X className="h-3 w-3" /></button>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex items-center gap-2">
        <input type="file" ref={fileRef} onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" accept=".csv,.txt,.tsv,.xlsx,.pdf" data-testid="ai-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="ai-attach-btn" title="Attach a file (CSV/Excel/PDF)"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-blue-300 hover:text-blue-600">
          <Paperclip className="h-4 w-4" />
        </button>
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-chat-input"
          placeholder={file ? "Add an instruction, e.g. 'add all customers'" : "Ask me anything, or attach a file..."}
          className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
        <button type="submit" data-testid="ai-chat-send" disabled={loading}
          className="flex h-10 w-10 items-center justify-center rounded-full brand-gradient-bg text-white disabled:opacity-50">
          <Send className="h-4 w-4" />
        </button>
      </form>
      {!compact && (
        <p className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
          <ShieldCheck className="h-3 w-3" /> Uses only your authorized data. No fabricated prices or bookings.
        </p>
      )}
    </div>
  );
}
